import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/LogoW.webp';

export default function VerifyOTP() {
  const { verifyOTP, resendOTP } = useAuth();
  const navigate = useNavigate();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputs = useRef([]);

  const email = sessionStorage.getItem('pixly_otp_email') || '';

  // Resend cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleDigit = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[i] = val;
    setDigits(next);
    if (val && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setDigits(paste.split(''));
      inputs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otp = digits.join('');
    if (otp.length < 6) { setError('Please enter all 6 digits'); return; }
    setError('');
    setLoading(true);
    try {
      const pendingBooking = JSON.parse(localStorage.getItem('pixly_pending_booking') || 'null');
      const user = await verifyOTP(email, otp, pendingBooking);
      localStorage.removeItem('pixly_pending_booking');
      sessionStorage.removeItem('pixly_otp_email');
      if (user?.role === 'admin') {
        navigate('/admin', { replace: true });
      } else if (user?.role === 'event_organizer') {
        navigate('/organizer', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    try {
      await resendOTP(email);
      setCooldown(60);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend OTP');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0B0F1F', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', fontFamily: 'Arial, Helvetica, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Link to="/"><img src={logo} alt="PixlyBooth" style={{ maxWidth: 160 }} /></Link>
        </div>

        <div style={{ background: 'linear-gradient(135deg, #101428 0%, #0D0F20 100%)', borderRadius: 18, padding: 32, border: '1px solid rgba(227,67,100,0.3)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
          <h1 style={{ margin: '0 0 4px', fontSize: 22, color: '#E34364', fontWeight: 700 }}>Check your email</h1>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: '#a0a0b8' }}>We sent a 6-digit code to</p>
          <p style={{ margin: '0 0 28px', fontSize: 14, color: '#fff', fontWeight: 600 }}>{email || 'your email'}</p>

          {error && (
            <div style={{ background: 'rgba(227,67,100,0.1)', border: '1px solid rgba(227,67,100,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#E34364', fontSize: 13 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} onPaste={handlePaste}>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 28 }}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={el => inputs.current[i] = el}
                  type="text" inputMode="numeric" maxLength={1}
                  value={d}
                  onChange={e => handleDigit(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  style={{ width: 48, height: 60, textAlign: 'center', fontSize: 28, fontWeight: 700, color: '#E34364', background: 'rgba(227,67,100,0.06)', border: '1px solid rgba(227,67,100,0.3)', borderRadius: 10, outline: 'none', boxSizing: 'border-box' }}
                />
              ))}
            </div>

            <p style={{ textAlign: 'center', fontSize: 12, color: '#a0a0b8', marginBottom: 24 }}>
              This code expires in <strong style={{ color: '#fff' }}>10 minutes</strong>
            </p>

            <button
              type="submit" disabled={loading}
              style={{ width: '100%', background: loading ? '#555' : '#E34364', color: '#fff', border: 'none', borderRadius: 50, padding: '14px 0', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Verifying…' : 'Verify Email →'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button
              onClick={handleResend} disabled={cooldown > 0}
              style={{ background: 'none', border: 'none', cursor: cooldown > 0 ? 'not-allowed' : 'pointer', color: cooldown > 0 ? '#555' : '#9F5CFF', fontSize: 13 }}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Didn't receive it? Resend"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
